// packages/core/src/learning/schools/real-return.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. (Worldview named no figures.)
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const REAL_RETURN_EXHIBIT: SchoolExhibitEntry = {
	id: 'real-return',
	tagline: 'Real return asks what your money can actually buy.',
	worldviewSummary:
		'Real return is paying attention to the gap between looking richer and actually being richer. This lens protects you from celebrating nominal gains while inflation, taxes, fees, and lifestyle costs quietly eat the result. It accepts that a dollar amount is not the full story because purchasing power is what changes your real life. The blind spot is that real return thinking can become too cautious if it makes every gain feel suspicious.',
	coreBeliefs: [
		'Real return believes the number on the screen is not the same as purchasing power.',
		'Real return believes inflation is not an abstract topic when it changes rent, food, insurance, and everyday costs.',
		'Real return believes taxes, fees, and drag matter because small leaks can reshape a long path.',
		'Real return believes a raise, gain, or portfolio increase only matters after you ask what it buys.',
		'Real return believes wealth should be measured against real life costs, not just bigger account balances.',
	],
	warnsAgainst: [
		'Celebrating nominal gains while your cost of living rises faster.',
		'Ignoring taxes and fees because the headline return looks good.',
		'Confusing income growth with freedom when lifestyle costs rise at the same time.',
		'Treating inflation as background noise instead of a force that changes real options.',
	],
	helps: 'This lens helps people who want to think clearly about purchasing power, real progress, and the hidden drag behind impressive looking numbers. It may not fit when someone needs simple motivation first, because real return thinking can feel sobering before it feels empowering.',
	figures: [],
	canon: [
		{
			title: 'Purchasing power',
			oneLineIdea:
				'The real question is not how many dollars you have, it is what those dollars can buy.',
		},
		{
			title: 'Inflation adjusted return',
			oneLineIdea:
				'A gain only becomes real progress after you compare it against rising prices.',
		},
		{
			title: 'Money illusion',
			oneLineIdea:
				'People often react to bigger numbers without noticing that the buying power may not have improved.',
		},
		{
			title: 'After tax thinking',
			oneLineIdea:
				'The amount you keep matters more than the amount that appears before taxes.',
		},
		{
			title: 'Fee drag',
			oneLineIdea:
				'Small ongoing costs can quietly reduce the part of growth that actually belongs to you.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-real-return',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare your progress against your real cost of living',
			action: { type: 'openDashboard', label: 'Open your Dashboard' },
		},
		{
			label: 'See whether your savings rate still buys future freedom',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Test a future where costs rise faster than expected',
			action: {
				type: 'openSimulator',
				label: 'Simulate higher costs',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write what real progress means beyond a bigger number',
			action: {
				type: 'noToolAction',
				label: 'Define real progress',
				prompt: 'Write one sentence describing what progress means after inflation, taxes, fees, and lifestyle costs.',
			},
		},
	],
	marketingHooks: [
		{ line: 'A bigger number is not always a better life.', kind: 'hook' },
		{
			line: 'Real return asks the question most people skip: what does this actually buy?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost is celebrating gains while your life gets more expensive.',
			kind: 'hook',
		},
		{
			line: 'When did you realize a raise did not feel like real progress?',
			kind: 'interview-question',
		},
		{
			line: 'What expense made you understand inflation in real life, not in theory?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users test whether their money is growing in dollars or growing in options.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Most finance apps show balances. Nisk teaches what those balances actually mean.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show someone a nominal gain, then subtract rising costs, taxes, and fees on camera.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people whether they feel richer now, or just surrounded by bigger prices.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
